import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import SkillsList from "../../features/manageSkills/skillsList";
import SkillsTopbar from "../../features/manageSkills/topbar";
import { useEffect, useState } from "react";
import {
  useCreateSkillMutation,
  useUpdateSkillMutation,
  useLazyDeleteSkillQuery,
} from "../../services/skills";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import SkillDetails from "../../Modals/skillDetails";
import usePermission from "../../hooks/usePermission";
import useTranslation from "../../hooks/traslation";
import useSkills from "../../hooks/useSkills";
import type { PageChangeHandler, SkillTableRow } from "../../types/api";
import { getErrorMessage } from "../../types/api";
import type { SkillListItem } from "../../reducers/skillsSlice";

const ManageSkills = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [createSkill] = useCreateSkillMutation();
  const [updateSkillMutation] = useUpdateSkillMutation();
  const [deleteSkill] = useLazyDeleteSkillQuery();
  const [selectedId, setSelectedId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);

  // Use skills hook for data fetching and Redux management
  const {
    skills: reduxSkillsList,
    isLoading: isSkillsLoading,
    error: skillsError,
    addSkillToStore,
    updateSkillInStore,
    removeSkillFromStore,
  } = useSkills({ fetchOnMount: true });

  const [name, setName] = useState<string>("");
  const [nameAr, setNameAr] = useState<string>("");
  const { isAdd, isDelete } = usePermission({
    label: "Manage Skills",
    page_permission: "isView",
  });

  const [allRows, setAllRows] = useState<SkillTableRow[]>([]);
  const [filteredRows, setFilteredRows] = useState<SkillTableRow[]>([]);
  const [rows, setRows] = useState<SkillTableRow[]>([]);

  const [page, setPage] = useState<number>(1);
  const itemsPerPage = 10;
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  const translate = useTranslation();

  const handlePageChange: PageChangeHandler = (event, newPage) => {
    setPage(newPage);
  };

  // Sync allRows with Redux skills list
  useEffect(() => {
    if (reduxSkillsList.length > 0) {
      setAllRows(reduxSkillsList);
    }
  }, [reduxSkillsList]);

  // Show error if skills fetch failed
  useEffect(() => {
    if (skillsError) {
      showError(translate?.auth?.failedToLoadSkills || skillsError);
    }
  }, [skillsError]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  // Frontend filtering and pagination
  useEffect(() => {
    // Filter data based on search term
    let filtered = allRows;
    if (debouncedSearchTerm.trim()) {
      filtered = allRows.filter((row: SkillTableRow) =>
        row?.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }
    setFilteredRows(filtered);

    // Paginate filtered results
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRows = filtered.slice(startIndex, endIndex);
    setRows(paginatedRows);
  }, [allRows, debouncedSearchTerm, page]);

  // Create Skill
  const handleSubmit = async () => {
    setIsFormLoading(true);
    const body = {
      name: name,
      name_ar: nameAr,
    };

    if (selectedId) {
      // Update Skill
      try {
        const response = await updateSkillMutation({
          body,
          id: selectedId,
        }).unwrap();
        if (response?.statusCode === 200) {
          const updated = response?.data ?? body;
          const updatedRow = { ...updated, _id: selectedId, id: selectedId };
          setAllRows((prev) =>
            prev.map((r) => (r._id === selectedId ? { ...r, ...updatedRow } : r))
          );
          updateSkillInStore(selectedId, updatedRow);
          showToast(
            translate?.auth?.skillUpdatedSuccess ||
              "Skill updated successfully"
          );
          setOpen(false);
          resetForm();
        }
      } catch (error: unknown) {
        console.log("error: ", error);
        showError(translate?.auth?.failToUpdate || "Failed to update skill");
      } finally {
        setIsFormLoading(false);
      }
    } else {
      // Create Skill
      try {
        const response = await createSkill({ data: body }).unwrap();
        if (response?.statusCode === 200) {
          const created = response?.data;
          if (created) {
            const newRow: SkillListItem = {
              ...created,
              id: created._id,
              _id: created._id,
            };
            setAllRows((prev) => [...prev, newRow]);
            addSkillToStore(newRow);
          }
          showToast(
            translate?.auth?.skillCreatedSuccess ||
              "Skill created successfully"
          );
          setOpen(false);
          resetForm();
        }
      } catch (error: unknown) {
        showError(getErrorMessage(error, "Failed to create skill"));
      } finally {
        setIsFormLoading(false);
      }
    }
  };

  const resetForm = () => {
    setName("");
    setNameAr("");
    setSelectedId("");
  };

  // Delete Skill
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await deleteSkill({ id: selectedId }).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        showToast(
          translate?.auth?.skillDeletedSuccess ||
            "Skill deleted successfully"
        );
        removeSkillFromStore(selectedId);
        // Reset to page 1 if current page becomes empty after deletion
        const remainingCount = filteredRows.length - 1;
        if (remainingCount > 0 && rows.length === 1 && page > 1) {
          const maxPage = Math.ceil(remainingCount / itemsPerPage);
          setPage(Math.min(page - 1, maxPage));
        }
        setAllRows((prev) => prev.filter((r) => r._id !== selectedId));
        setOpenDelete(false);
        setSelectedId("");
      }
    } catch {
      showError("Failed to delete skill");
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
    resetForm();
  };

  const handleCloseDetailsModal = () => {
    setOpenDetails(false);
    setSelectedId("");
  };

  const handleEditClick = (row: { _id: string; name?: string; name_ar?: string }) => {
    setSelectedId(row._id);
    setName(row.name || "");
    setNameAr(row.name_ar || "");
    setOpen(true);
  };

  return (
    <MainContainer isLoading={isSkillsLoading}>
      <div className="main_title">
        <h1>Manage Skills</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translate?.dashboard?.dashboard || "Dashboard"}
          </span>{" "}
          Manage Skills
        </p>
      </div>

      <div className="cards">
        <SkillsTopbar
          name={name}
          setName={setName}
          handleSubmit={handleSubmit}
          setOpen={setOpen}
          open={open}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          setSearchTerm={setSearchTerm}
          setDebouncedSearchTerm={setDebouncedSearchTerm}
          searchTerm={searchTerm}
          isFormLoading={isFormLoading}
          nameAr={nameAr}
          setNameAr={setNameAr}
        />
        <SkillsList
          setSelectedId={setSelectedId}
          setOpen={setOpen}
          open={open}
          name={name}
          setName={setName}
          openDelete={openDelete}
          setOpenDelete={setOpenDelete}
          rows={rows}
          setRows={setRows}
          openDetails={openDetails}
          setOpenDetails={setOpenDetails}
          isLoading={isLoading}
          handleEditClick={handleEditClick}
          handlePageChange={handlePageChange}
          setPage={setPage}
          page={page}
          totalCount={filteredRows.length}
          setNameAr={setNameAr}
        />
      </div>
      <ConfirmationModal
        text={translate?.auth?.confirmDeleteSkill || "Are you sure you want to delete this skill?"}
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDelete}
        isLoading={isLoading}
      />
      <SkillDetails
        open={openDetails}
        name={name}
        onClose={handleCloseDetailsModal}
        selectedId={selectedId}
        nameAr={nameAr}
      />
    </MainContainer>
  );
};

export default ManageSkills;
